'use client';
import { useQuery } from '@tanstack/react-query';

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { usePathname, useRouter } from '@/hooks';

import { globalVm } from '@/pages/GlobalPageCtrl';
import { getUserInfo } from '@/services';
import { ICurrentUser } from '@/types';
import { getEnsName, WalletType } from '@/utils';

// import TonWeb from "tonweb";

export const getUserData = async () => {
  const user = await getUserInfo();
  // if (user?.chainType === WalletType.TON) {
  // const tonweb = new TonWeb();
  // tonweb.dns.getWalletAddress('test.ton');
  // }
  user.walletAddress = user.displayAddress;
  return user;
};

export const NO_NEED_LOGIN_PATH = [
  '/signin',
  '/verify',
  '/callback/sg',
  '/bizify-demo',
  '/share',
  '/download',
  '/preview'
];

export const useFetchUser = () => {
  const pathname = usePathname() as string;
  const needAuth = !NO_NEED_LOGIN_PATH.find((p) => {
    return pathname.startsWith(p);
  });
  const [ens, setEns] = useState('');

  const {
    data,
    refetch: fetchUser,
    isLoading,
    isFetched
  } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUserData(),
    retry: false,
    enabled: false,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (needAuth) {
      fetchUser();
    }
  }, [needAuth]);

  const getEns = async () => {
    if (data?.chainType === WalletType.EVM) {
      const ensName = await getEnsName(data?.address as `0x${string}`);
      setEns(ensName || '');
    }
  };

  useEffect(() => {
    if (data?.address) {
      getEns();
    }
  }, [data]);

  const user = useMemo(() => {
    return data
      ? {
          ...data,
          ens
        }
      : undefined;
  }, [data, ens]);

  return {
    user,
    fetchUser,
    isFetched,
    isLoading,
    needAuth
  };
};

interface UserInfoContextProps {
  user?: ICurrentUser;
  isLoading: boolean;
  fetchUser: () => void;
  needAuth?: boolean;
}

const UserInfoContext = createContext<UserInfoContextProps>({
  user: undefined,
  isLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  fetchUser: () => {},
  needAuth: true
});

export const UserInfoProvider = ({ children }: { children: any }) => {
  const { user, isLoading, fetchUser, needAuth } = useFetchUser();
  const [chainId, setChainId] = useState(0);
  const handleChainChanged = function (chainId: string) {
    const id = Number(chainId);
    globalVm.toggleBeraChainModal(id === 80085);
    setChainId(id);
  };
  useEffect(() => {
    if (!user || !needAuth || user.chainType !== WalletType.EVM || !window.ethereum) {
      window.ethereum?.off('chainChanged', handleChainChanged);
      return;
    }
    setChainId(Number(window.ethereum.chainId || 0));
    window.ethereum?.on('chainChanged', handleChainChanged);
    return () => {
      window.ethereum?.off('chainChanged', handleChainChanged);
    };
  }, [user]);
  return (
    <UserInfoContext.Provider value={{ user: user ? { ...user, chainId } : user, isLoading, fetchUser, needAuth }}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => {
  return useContext(UserInfoContext);
};

export const usePermission = (role: string) => {
  const { user } = useUserInfo();
  return user?.roles?.includes(role);
};

export const AuthWrapper = ({ children, role }: { children: ReactNode; role: string }) => {
  const hasPermission = usePermission(role);
  const router = useRouter();
  if (!hasPermission) {
    return router.replace('/dashboard');
  }
  return children;
};
